import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, Pagination, Select as AntSelect } from "antd";
import { Download } from "lucide-react";
import { Button } from "../../components/ui/button";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { PtsLeaveApprovalService } from "@/services/APIs";
import type { LeaveApprovalStatus } from "@/services/APIs/PtsLeaveApprovalService";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import LeaveRequestFilters from "./LeaveRequestFilters";
import {
  EMPTY_LEAVE_REQUEST_FILTERS,
  type LeaveRequestFilterValues,
} from "./LeaveRequestFilters.constants";
import {
  formatDateDmy,
  formatDateYmd,
  leaveStatusBadgeClass,
  leaveStatusLabel,
  leaveTypeLabel,
  resolveName,
} from "./utils";
import { useDragRowSelection } from "../../hooks/useDragRowSelection";

const MANAGEMENT_STATUSES: LeaveApprovalStatus[] = [
  "pending",
  "approved",
  "reject",
];

const BULK_STATUS_OPTIONS = [
  { value: "Approved", label: "Approved" },
  { value: "Reject", label: "Reject" },
  { value: "Pending", label: "Pending" },
];

type ManagementTabProps = {
  employeeNameById: Map<string, string>;
};

const ManagementTab = ({ employeeNameById }: ManagementTabProps) => {
  const queryClient = useQueryClient();
  const [filterValues, setFilterValues] = useState<LeaveRequestFilterValues>(
    EMPTY_LEAVE_REQUEST_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data, isLoading } = useQuery({
    queryKey: [
      "leave-approvals",
      "management-list",
      filterValues.startDate,
      filterValues.endDate,
      filterValues.leaveType,
      filterValues.status,
      filterValues.search,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsLeaveApprovalService.getLeaveApprovals({
        page: currentPage,
        page_size: pageSize,
        start_date: filterValues.startDate,
        end_date: filterValues.endDate,
        leave_type: filterValues.leaveType,
        status: filterValues.status,
        search: filterValues.search,
      }),
  });

  const recentRequests = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const stats = data?.cards ?? {
    pending: 0,
    approved: 0,
    reject: 0,
    wfh: 0,
    full_day: 0,
    half_day: 0,
  };

  const {
    selectedIds,
    setSelectedIds,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleMouseDown,
    handleMouseEnter,
  } = useDragRowSelection(recentRequests);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [filterValues, setSelectedIds]);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, pageSize, setSelectedIds]);

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: string }) =>
      PtsLeaveApprovalService.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      notify.success("Leave requests status updated successfully");
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["leave-approvals"] });
    },
    onError: (error: any) => {
      notify.error(
        error?.message ?? "Failed to update status for selected items"
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeaveApprovalStatus }) =>
      PtsLeaveApprovalService.updateLeaveApproval(id, { status }),
    onSuccess: result => {
      if (!result?.id) {
        return;
      }
      notify.success("Leave request status updated");
      queryClient.invalidateQueries({ queryKey: ["leave-approvals"] });
    },
  });

  const handleExportCSV = async () => {
    try {
      const res = await PtsLeaveApprovalService.getLeaveApprovals({
        page_size: 100000,
        start_date: filterValues.startDate,
        end_date: filterValues.endDate,
        leave_type: filterValues.leaveType,
        status: filterValues.status,
        search: filterValues.search,
      });

      const items = res.results;

      const escapeCsv = (val: unknown) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "Start Date",
        "End Date",
        "Leave Type",
        "Reason",
        "Requested By Name",
        "Status",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const reqName =
          item.requested_by_name ??
          resolveName(item.requested_by, employeeNameById);

        return [
          escapeCsv(formatDateDmy(item.start_date)),
          escapeCsv(formatDateDmy(item.end_date)),
          escapeCsv(leaveTypeLabel(item.leave_type)),
          escapeCsv(item.reason),
          escapeCsv(reqName),
          escapeCsv(item.status),
          escapeCsv(formatDateDmy(item.created_on)),
          escapeCsv(formatDateDmy(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `leave-requests-management-${formatDateYmd(new Date().toISOString())}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Management dashboard
        </h1>
        <Button
          type="button"
          variant="default"
          onClick={handleExportCSV}
          className="h-10 px-5"
        >
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <LeaveRequestFilters
        values={filterValues}
        onChange={setFilterValues}
        idPrefix="management-leave-request"
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="mt-2 text-3xl font-bold text-amber-500">
              {stats.pending}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="mt-2 text-3xl font-bold text-emerald-600">
              {stats.approved}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Reject</p>
            <p className="mt-2 text-3xl font-bold text-rose-600">
              {stats.reject}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Work From Home</p>
            <p className="mt-2 text-3xl font-bold text-sky-600">{stats.wfh}</p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Full Day</p>
            <p className="mt-2 text-3xl font-bold text-indigo-600">
              {stats.full_day}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Half Day</p>
            <p className="mt-2 text-3xl font-bold text-teal-600">
              {stats.half_day}
            </p>
          </CardContent>
        </Card>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 rounded-lg border border-[#BAE0FF] bg-[#F0F7FF] px-4 py-2.5">
          <span className="text-sm font-medium text-gray-800">
            {selectedIds.length} selected
          </span>
          <div className="w-44">
            <AntSelect
              value={undefined}
              placeholder="Change Status"
              disabled={bulkStatusMutation.isPending}
              loading={bulkStatusMutation.isPending}
              onChange={value => {
                if (value) {
                  bulkStatusMutation.mutate({
                    ids: selectedIds,
                    status: value,
                  });
                }
              }}
              className="w-full"
              options={BULK_STATUS_OPTIONS}
            />
          </div>
          <button
            type="button"
            onClick={() => setSelectedIds([])}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="w-[48px] px-4 py-3 text-center">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      disabled={isLoading || recentRequests.length === 0}
                    />
                  </th>
                  <th className="px-5 py-3 font-medium">Start Date</th>
                  <th className="px-5 py-3 font-medium">End Date</th>
                  <th className="px-5 py-3 font-medium">Leave Type</th>
                  <th className="px-5 py-3 font-medium">Reason</th>
                  <th className="px-5 py-3 font-medium">Requested By</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Created On</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading leave requests...
                    </td>
                  </tr>
                ) : recentRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No leave requests found.
                    </td>
                  </tr>
                ) : (
                  recentRequests.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td
                        className="w-[48px] cursor-pointer select-none px-4 py-3.5 text-center"
                        onMouseDown={e => handleMouseDown(item.id, index, e)}
                        onMouseEnter={() => handleMouseEnter(item.id, index)}
                      >
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          className="pointer-events-none"
                        />
                      </td>
                      <td className="px-5 py-3.5 font-medium text-[#111827]">
                        {formatDateDmy(item.start_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.end_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {leaveTypeLabel(item.leave_type)}
                      </td>
                      <td className="max-w-[260px] truncate px-5 py-3.5 text-[#374151]">
                        {item.reason ?? "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.requested_by_name ??
                          resolveName(item.requested_by, employeeNameById)}
                      </td>
                      <td className="px-5 py-3.5">
                        <Select
                          value={String(item.status || "pending")}
                          disabled={
                            statusMutation.isPending &&
                            statusMutation.variables?.id === item.id
                          }
                          onValueChange={status =>
                            statusMutation.mutate({
                              id: item.id,
                              status,
                            })
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              "h-8 w-[140px] rounded-full px-3 text-xs font-medium shadow-none focus:ring-1 focus:ring-offset-0",
                              leaveStatusBadgeClass(item.status)
                            )}
                          >
                            <SelectValue>
                              {leaveStatusLabel(item.status)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {MANAGEMENT_STATUSES.map(status => (
                              <SelectItem key={status} value={status}>
                                {leaveStatusLabel(status)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.created_on)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  if (size && size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                    return;
                  }
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={["50", "100", "200", "500"]}
                defaultPageSize={50}
                disabled={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagementTab;
